import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  MessageCircle,
  Ruler,
  ShieldCheck,
  Sparkles,
  Truck,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";

import ProductCard from "../components/ProductCard";
import useSEO from "../lib/useSEO";
import { fetchProduct, imageUrl } from "../lib/api";
import { BRAND, FORMAT_IDR, buildWhatsAppLink } from "../lib/constants";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState("M");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setLoading(true);
    fetchProduct(slug)
      .then((d) => {
        setData(d);
        setActiveImg(0);
        setSize(d.product.size_guide?.[1]?.size || "M");
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const product = data?.product;

  useSEO(
    product
      ? {
          title: `${product.name} — ${product.category_name} | ${BRAND.short}`,
          description: product.short_description,
          keywords: `${product.name.toLowerCase()}, ${product.category_name.toLowerCase()}, custom, sublimasi`,
          image: product.images?.[0]?.url,
          canonical: window.location.origin + `/shop/${product.slug}`,
          jsonLd: {
            id: "product-jsonld",
            data: {
              "@context": "https://schema.org",
              "@type": "Product",
              name: product.name,
              description: product.description,
              image: product.images?.map(img => imageUrl(img.url)),
              brand: { "@type": "Brand", name: BRAND.name },
              category: product.category_name,
              offers: {
                "@type": "Offer",
                priceCurrency: "IDR",
                price: product.price_from,
                availability: "https://schema.org/InStock",
                url: window.location.href,
              },
            },
          },
        }
      : { title: "Produk — " + BRAND.short }
  );

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid md:grid-cols-2 gap-10">
          <div className="aspect-[4/5] bg-[#0a0a0a] border border-[#262626] animate-pulse" />
          <div className="space-y-4">
            <div className="h-10 w-3/4 bg-[#0a0a0a] animate-pulse" />
            <div className="h-4 w-1/2 bg-[#0a0a0a] animate-pulse" />
            <div className="h-32 w-full bg-[#0a0a0a] animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
        <h1 className="font-display text-4xl uppercase text-white">
          Produk tidak ditemukan
        </h1>
        <Link to="/shop" className="btn-ghost mt-6 inline-flex">
          Kembali ke Shop
        </Link>
      </div>
    );
  }

  const waMsg = `Halo Admin Ace Sublimasi Nusantara, saya mau order *${product.name}* (Size ${size}, ${qty} pcs). Mohon info detailnya.`;

  return (
    <div data-testid="product-detail-page" className="border-b border-[#1f1f1f]">
      {/* Breadcrumb */}
      <div className="border-b border-[#1f1f1f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-2 text-xs uppercase tracking-widest text-[#A3A3A3]">
          <Link to="/" className="hover:text-white">
            Beranda
          </Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/shop" className="hover:text-white">
            Shop
          </Link>
          <ChevronRight className="w-3 h-3" />
          <Link
            to={`/shop?category=${product.category}`}
            className="hover:text-white"
          >
            {product.category_name}
          </Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-white truncate">{product.name}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14 grid md:grid-cols-12 gap-8 lg:gap-12">
        {/* Gallery */}
        <div className="md:col-span-7">
          <div
            data-testid="product-main-image"
            className="border border-[#262626] aspect-[4/5] overflow-hidden bg-[#0a0a0a]"
          >
            <img
                src={imageUrl(product.images[activeImg]?.url)}
                alt={product.name}
                className="w-full h-full object-cover"
            />
          </div>
          {product.images?.length > 1 && (
            <div className="mt-4 grid grid-cols-4 gap-3">
              {product.images?.map((img, i) => (
                <button
                  key={i}
                  data-testid={`thumb-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-square overflow-hidden border transition-colors ${
                    activeImg === i
                      ? "border-[#CCFF00]"
                      : "border-[#262626] hover:border-[#A3A3A3]"
                  }`}
                >
                  <img
                    src={imageUrl(img.url)}
                    alt={`${product.name} ${i + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="md:col-span-5">
          <div className="md:sticky md:top-24">
            <div className="flex flex-wrap gap-2 mb-3">
              {product.badges.map((b) => (
                <span
                  key={b}
                  className={`text-[10px] tracking-[0.25em] uppercase px-2 py-1 font-bold ${
                    b === "BEST SELLER"
                      ? "bg-[#FF3B30] text-white"
                      : "bg-[#CCFF00] text-black"
                  }`}
                >
                  {b}
                </span>
              ))}
            </div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#A3A3A3]">
              {product.category_name}
            </p>
            <h1
              data-testid="product-name"
              className="font-display text-4xl sm:text-5xl uppercase leading-none mt-2 text-white"
            >
              {product.name}
            </h1>
            <div className="mt-4 flex items-baseline gap-3">
              <span className="text-xs uppercase tracking-widest text-[#A3A3A3]">
                Mulai dari
              </span>
              <span
                data-testid="product-price"
                className="font-display text-3xl text-[#CCFF00]"
              >
                {FORMAT_IDR(product.price_from)}
              </span>
            </div>

            <p className="mt-5 text-base text-[#C2C2C2] leading-relaxed">
              {product.description}
            </p>

            {/* Size */}
            <div className="mt-7">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-widest text-[#CCFF00]">
                  Pilih Size
                </p>
                <Dialog>
                  <DialogTrigger asChild>
                    <button
                      data-testid="size-guide-trigger"
                      className="text-xs uppercase tracking-widest text-white hover:text-[#CCFF00] flex items-center gap-1"
                    >
                      <Ruler className="w-3.5 h-3.5" /> Size Guide
                    </button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0a0a0a] border border-[#262626] rounded-none max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="font-display uppercase text-white text-2xl">
                        Size Guide
                      </DialogTitle>
                      <DialogDescription className="text-[#A3A3A3]">
                        Ukuran dapat sedikit berbeda 1–2 cm karena custom production. Konsultasi admin kalau ragu.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="border border-[#262626] mt-2">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-[#262626] bg-[#0e0e0e]">
                            <th className="p-3 text-left text-[#CCFF00] uppercase tracking-widest text-xs">
                              Size
                            </th>
                            <th className="p-3 text-left text-[#CCFF00] uppercase tracking-widest text-xs">
                              Lebar Dada
                            </th>
                            <th className="p-3 text-left text-[#CCFF00] uppercase tracking-widest text-xs">
                              Panjang
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {product.size_guide.map((s) => (
                            <tr
                              key={s.size}
                              className="border-b border-[#262626] last:border-b-0 text-white"
                            >
                              <td className="p-3 font-bold">{s.size}</td>
                              <td className="p-3">{s.chest}</td>
                              <td className="p-3">{s.length}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {product.size_guide.map((s) => (
                  <button
                    key={s.size}
                    data-testid={`size-option-${s.size}`}
                    onClick={() => setSize(s.size)}
                    className={`min-w-[56px] py-3 px-4 border text-sm font-bold uppercase tracking-widest transition-colors ${
                      size === s.size
                        ? "border-[#CCFF00] bg-[#CCFF00] text-black"
                        : "border-[#262626] text-white hover:border-[#CCFF00]"
                    }`}
                  >
                    {s.size}
                  </button>
                ))}
              </div>
            </div>

            {/* Qty */}
            <div className="mt-6">
              <p className="text-xs font-bold uppercase tracking-widest text-[#CCFF00]">
                Jumlah
              </p>
              <div className="mt-3 inline-flex items-stretch border border-[#262626]">
                <button
                  data-testid="qty-decrement"
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="w-10 text-white hover:bg-[#0e0e0e]"
                  aria-label="kurangi"
                >
                  −
                </button>
                <input
                  data-testid="qty-input"
                  type="number"
                  min="1"
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || "1", 10)))}
                  className="w-16 bg-transparent text-center text-white outline-none border-x border-[#262626] py-3"
                />
                <button
                  data-testid="qty-increment"
                  onClick={() => setQty(qty + 1)}
                  className="w-10 text-white hover:bg-[#0e0e0e]"
                  aria-label="tambah"
                >
                  +
                </button>
              </div>
            </div>

            {/* CTA */}
            <a
              href={buildWhatsAppLink(waMsg)}
              target="_blank"
              rel="noreferrer"
              data-testid="product-whatsapp-cta"
              className="btn-primary mt-7 w-full !py-5 text-base"
            >
              <MessageCircle className="w-5 h-5" />
              Order via WhatsApp
            </a>

            <div className="mt-5 grid grid-cols-3 gap-4 border-t border-[#262626] pt-5 text-xs">
              <div className="flex items-start gap-2 text-[#A3A3A3]">
                <ShieldCheck className="w-4 h-4 text-[#CCFF00] shrink-0 mt-0.5" />
                <span>Sublimasi premium tahan lama</span>
              </div>
              <div className="flex items-start gap-2 text-[#A3A3A3]">
                <Sparkles className="w-4 h-4 text-[#CCFF00] shrink-0 mt-0.5" />
                <span>Bebas custom desain</span>
              </div>
              <div className="flex items-start gap-2 text-[#A3A3A3]">
                <Truck className="w-4 h-4 text-[#CCFF00] shrink-0 mt-0.5" />
                <span>Kirim seluruh Indonesia</span>
              </div>
            </div>

            {/* Benefits */}
            <div className="mt-8">
              <p className="text-xs font-bold uppercase tracking-widest text-[#CCFF00]">
                Benefit Produk
              </p>
              <ul className="mt-3 space-y-2.5">
                {product.benefits.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-3 text-sm text-white"
                  >
                    <CheckCircle2 className="w-4 h-4 text-[#CCFF00] mt-0.5 shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>

            {/* Custom options */}
            <div className="mt-8">
              <p className="text-xs font-bold uppercase tracking-widest text-[#CCFF00]">
                Yang Bisa Di-Custom
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {product.custom_options.map((o) => (
                  <span
                    key={o}
                    className="border border-[#262626] px-3 py-1.5 text-xs text-white uppercase tracking-wider"
                  >
                    {o}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <section className="border-t border-[#1f1f1f] bg-[#070707]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-3">
              / FAQ {product.name}
            </p>
            <h2 className="font-display text-4xl uppercase text-white leading-none">
              Pertanyaan Soal Produk Ini
            </h2>
            <p className="mt-5 text-sm text-[#A3A3A3]">
              Belum nemu jawabannya? Chat admin di WhatsApp.
            </p>
          </div>
          <div className="md:col-span-7">
            <Accordion type="single" collapsible className="border border-[#262626]">
              {product.faqs.map((f, idx) => (
                <AccordionItem
                  key={idx}
                  value={`f-${idx}`}
                  className="border-b border-[#262626] last:border-b-0"
                >
                  <AccordionTrigger
                    data-testid={`product-faq-${idx}`}
                    className="px-5 md:px-6 py-5 text-left text-base text-white font-bold uppercase tracking-tight hover:text-[#CCFF00] hover:no-underline data-[state=open]:text-[#CCFF00]"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="px-5 md:px-6 pb-5 text-sm text-[#C2C2C2] leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Related */}
      {data?.related?.length > 0 && (
        <section className="border-t border-[#1f1f1f] py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-8">
              <h2 className="font-display text-3xl sm:text-4xl uppercase text-white leading-none">
                Produk Serupa
              </h2>
              <Link
                to={`/shop?category=${product.category}`}
                className="text-sm uppercase tracking-widest text-[#CCFF00] border-b border-[#CCFF00] pb-1 hover:gap-3 inline-flex items-center gap-2 transition-all"
              >
                Lihat Semua <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {data.related.map((p) => (
                <ProductCard key={p.slug} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
